import { Link } from "react-router-dom";
import { BOOKING_URL } from "@/data/services";
import { ArrowRight } from "lucide-react";
import { Reveal, SectionHeading, TickList } from "@/components/site/Reveal";
import { Sprig } from "@/components/site/Logo";

const QUALIFICATIONS = [
  "Accredited Mental Health Social Worker",
  "Registered Specialist Behaviour Support Practitioner",
  "Bachelor of Social Science (Psychology)",
  "Graduate Diploma of Family Dispute Resolution Practice",
  "Master of Social Work",
  "Master of Science in Medicine (Trauma-Informed Psychotherapy)",
  "Doctor of Arts (Psychology) — current candidate",
  "Member, Australian Association of Social Workers (AASW)",
];

const EXPERIENCE = [
  "Child protection since 2015 — investigation, assessment, risk formulation and complex case management",
  "Joint Child Protection Response Team, alongside the NSW Police Force",
  "Clinical Manager — headspace Beaudesert and headspace Dubbo",
  "Specialist mental health — neurodevelopmental differences, complex trauma, psychosis, personality-related difficulties, emotional dysregulation",
  "Founder, Eucalypt Therapy — 2023",
];

const POPULATIONS = [
  "Adults, adolescents and children",
  "Couples and families",
  "Current and former ADF members, veterans and defence families",
  "NDIS participants and their support networks",
  "Parents and professionals involved in family law matters",
];

const LOCATIONS = [
  "In person — Unit 12, 151 Cotlew Street, Ashmore QLD 4214 (Gold Coast)",
  "In person — Room 6, 310 Thesiger Court, Deakin ACT 2600 (Canberra)",
  "In-home visits across the Gold Coast, Logan and Brisbane",
  "Telehealth across Queensland, the ACT and Australia-wide where appropriate",
];

export default function About() {
  return (
    <main data-testid="about-page">
      <section className="relative overflow-hidden bg-white">
        <div className="pointer-events-none absolute -right-10 -top-10 text-euca-badge">
          <Sprig className="h-64 w-64" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 relative">
          <SectionHeading
            eyebrow="About"
            title="A calm, trauma-informed practice on the Gold Coast and in Canberra"
            lead="Eucalypt Therapy supports individuals, couples and families experiencing complex mental health, relationship, disability and family-system challenges — with warmth, structure and evidence-informed care."
          />
        </div>
      </section>

      <section className="relative overflow-hidden bg-euca-dark">
        <div className="pointer-events-none absolute -left-14 -bottom-16">
          <Sprig variant="dark" className="h-80 w-80" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 text-center relative" data-testid="mission-section">
          <Reveal>
            <Sprig variant="sage" className="mx-auto h-12 w-12" />
            <blockquote className="mt-8 font-serif text-2xl sm:text-3xl lg:text-4xl font-medium italic leading-snug text-euca-mist">
              &ldquo;The eucalypt tree, so unique to the Australian landscape, is not impervious to fire. But it
              weathers the hardship and looks to the fertile ground after the flames die down to grow back
              stronger.&rdquo;
            </blockquote>
            <p className="mx-auto mt-8 max-w-2xl text-base sm:text-lg leading-relaxed text-euca-mist/70">
              Our mission at Eucalypt Therapy is to empower our clients to see that every hardship is an opportunity
              for growth — and that at the heart of everyone is the potential to come back stronger, like the
              eucalypt.
            </p>
            <p className="mt-8 text-[11px] font-semibold uppercase tracking-[0.3em] text-euca-sage">Our mission</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-euca-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="grid items-start gap-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="flex flex-col items-center pt-4">
                  <div className="relative">
                    <div className="absolute -inset-4 rounded-full border border-euca-sage/50" aria-hidden="true" />
                    <img
                      src="/images/lucie.png"
                      alt="Lucie Tursicova, Accredited Mental Health Social Worker"
                      className="h-72 w-72 rounded-full object-cover shadow-xl shadow-euca-dark/10 sm:h-96 sm:w-96"
                      data-testid="lucie-photo"
                    />
                  </div>
                  <p className="mt-10 text-center text-sm italic text-mutedsoft">
                    Luciana (Lucie) Tursicova — Accredited Mental Health Social Worker · Specialist Behaviour Support Practitioner
                  </p>
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <SectionHeading eyebrow="Meet Lucie" title="Experience you can lean on" />
              <Reveal delay={0.1}>
                <p className="mt-6 text-base sm:text-lg leading-relaxed text-mutedsoft">
                  Luciana (Lucie) Tursicova is an Accredited Mental Health Social Worker and registered Specialist
                  Behaviour Support Practitioner. Her areas of practice include complex trauma, complex mental
                  health presentations, neurodivergence, personality disorders, psychosis, child protection, family
                  functioning, and the assessment and support of people presenting with complex psychosocial and
                  behavioural needs.
                </p>
                <p className="mt-4 text-base sm:text-lg leading-relaxed text-mutedsoft">
                  Luciana holds a Bachelor of Social Science (Psychology), a Graduate Diploma of Family Dispute
                  Resolution Practice, a Master of Social Work and a Master of Science in Medicine (Trauma-Informed
                  Psychotherapy), and is currently a Doctor of Arts (Psychology) candidate.
                </p>
                <p className="mt-4 text-base sm:text-lg leading-relaxed text-mutedsoft">
                  Luciana has worked within the child protection sector since 2015 — including alongside the NSW
                  Police Force within a Joint Child Protection Response Team — and previously held Clinical Manager
                  positions with headspace Beaudesert and headspace Dubbo. She established Eucalypt Therapy in 2023.
                </p>
                <p className="mt-4 text-base sm:text-lg leading-relaxed text-mutedsoft">
                  Her approach is collaborative and paced to the individual — drawing on trauma-informed
                  psychotherapy, mental health social work, specialist behaviour support and multidisciplinary
                  collaboration, and adapted to the person, couple or family in front of her.
                </p>
              </Reveal>
              <div className="mt-10 grid gap-10 sm:grid-cols-2">
                <Reveal delay={0.15}>
                  <h3 className="font-serif text-xl font-medium text-euca-dark">Qualifications & accreditation</h3>
                  <div className="mt-4">
                    <TickList items={QUALIFICATIONS} columns={1} />
                  </div>
                </Reveal>
                <Reveal delay={0.2}>
                  <h3 className="font-serif text-xl font-medium text-euca-dark">Professional history</h3>
                  <div className="mt-4">
                    <TickList items={EXPERIENCE} columns={1} />
                  </div>
                </Reveal>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-euca-line bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 text-center" data-testid="about-quote">
          <Reveal>
            <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-euca">About us</p>
            <blockquote className="mt-7 font-serif text-2xl sm:text-3xl font-medium italic leading-snug text-euca-dark">
              &ldquo;All wonderful things come with struggle and baggage. Life was never meant to be perfect. The
              journey was never meant to be easy. But every time it is difficult, remind yourself to breathe, in
              the same way you breathed through the struggle before. You survived then, and you can certainly
              survive some more.&rdquo;
            </blockquote>
            <p className="mt-7 text-sm text-mutedsoft">&mdash; &lsquo;Hope in the Morning&rsquo;, Courtney Peppernell</p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-euca-line bg-euca-mist">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24" data-testid="practice-philosophy">
          <SectionHeading eyebrow="The practice" title="Care that sees the whole person" />
          <Reveal delay={0.08}>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-mutedsoft">
              Eucalypt Therapy is a trauma-informed therapeutic and behavioural support practice committed to
              respectful, evidence-informed and person-centred services for people experiencing mental health,
              psychosocial, behavioural and disability-related support needs.
            </p>
            <p className="mt-4 text-base sm:text-lg leading-relaxed text-mutedsoft">
              We recognise that behaviour occurs within a broader biological, psychological, interpersonal, social
              and environmental context — so we seek to understand the person and the function of behaviour, rather
              than viewing behaviours of concern in isolation.
            </p>
            <p className="mt-4 text-base sm:text-lg leading-relaxed text-mutedsoft">
              Our work focuses on identifying and addressing underlying needs, strengthening communication and
              emotional regulation skills, improving relationships and participation, and ensuring the people
              providing support have practical, consistent strategies for everyday environments.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="grid gap-14 lg:grid-cols-2">
            <div>
              <SectionHeading eyebrow="Who we support" title="Populations supported" />
              <div className="mt-8">
                <TickList items={POPULATIONS} columns={1} />
              </div>
            </div>
            <div>
              <SectionHeading eyebrow="Where we work" title="Service locations" />
              <div className="mt-8">
                <TickList items={LOCATIONS} columns={1} />
              </div>
              <Reveal delay={0.15}>
                <a
                  href="/book"
                  data-testid="about-book-button"
                  className="mt-10 inline-flex items-center gap-2 rounded-full bg-euca px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-euca-dark"
                >
                  Book an Appointment <ArrowRight className="h-4 w-4" />
                </a>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
